/**
 * Event index builder — turns the adapter output (`CalendarEvent[]`,
 * `CalendarResource[]`) into normalized, laid-out, indexed structures.
 *
 * Everything downstream (rendering, keyboard navigation, hit-testing,
 * highlight tiers) reads from these indexes. Rebuilds are O(n log n) in the
 * event count and happen only when the authoritative snapshot changes.
 */
import type {
  CalendarBusinessHours,
  CalendarEvent,
  CalendarResource,
} from "../../Calendar/controller/adapters/types";
import {
  isGroupPatientType,
  isGroupTherapistType,
} from "../../Calendar/controller/adapters/types";
import {
  MCBackgroundSegment,
  MCEvent,
  MCGridConfig,
  MCResource,
  MCTimeRange,
} from "./types";
import { kindOfEvent } from "./eventKind";
import { minutesToTime } from "./gridGeometry";

export interface MCEventIndex {
  /** Foreground events with lane layout applied, sorted by (resource, start) */
  events: ReadonlyArray<MCEvent>;
  background: ReadonlyArray<MCBackgroundSegment>;
  byId: ReadonlyMap<string, MCEvent>;
  /** Per resource, sorted by startMin (stable references per rebuild) */
  byResource: ReadonlyMap<string, ReadonlyArray<MCEvent>>;
  backgroundByResource: ReadonlyMap<string, ReadonlyArray<MCBackgroundSegment>>;
  /** patientId -> event ids (highlight tier 1) */
  byPatientId: ReadonlyMap<number, ReadonlyArray<string>>;
  /** appointmentId -> event ids (jump target, multi-slot ops) */
  byAppointmentId: ReadonlyMap<string, ReadonlyArray<string>>;
  /** Milliseconds spent building (perf HUD) */
  buildMs: number;
}

const EMPTY_EVENTS: ReadonlyArray<MCEvent> = Object.freeze([]);
const EMPTY_SEGMENTS: ReadonlyArray<MCBackgroundSegment> = Object.freeze([]);

// ---------------------------------------------------------------------------
// Resource normalization
// ---------------------------------------------------------------------------

const parseTimeToMin = (value?: string): number | null => {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

/** Merge overlapping/adjacent ranges (input need not be sorted). */
const mergeRanges = (ranges: MCTimeRange[]): MCTimeRange[] => {
  if (ranges.length <= 1) return ranges;
  const sorted = [...ranges].sort((a, b) => a.startMin - b.startMin);
  const merged: MCTimeRange[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, sorted[i].endMin);
    } else {
      merged.push({ ...sorted[i] });
    }
  }
  return merged;
};

/**
 * Business ranges for the displayed weekday, from FullCalendar-style
 * businessHours entries ({ daysOfWeek, startTime, endTime }).
 */
const businessRangesForDay = (
  businessHours: CalendarBusinessHours | undefined,
  weekday: number,
  cfg: MCGridConfig,
): MCTimeRange[] => {
  if (!businessHours?.length) return [];
  const ranges: MCTimeRange[] = [];
  for (const entry of businessHours) {
    const applies =
      !entry.daysOfWeek ||
      entry.daysOfWeek.length === 0 ||
      entry.daysOfWeek.some((d) => Number(d) === weekday);
    if (!applies) continue;
    const start = parseTimeToMin(entry.startTime);
    const end = parseTimeToMin(entry.endTime);
    if (start == null || end == null || end <= start) continue;
    ranges.push({
      startMin: Math.max(start, cfg.dayStartMin),
      endMin: Math.min(end, cfg.dayEndMin),
    });
  }
  return mergeRanges(ranges.filter((r) => r.endMin > r.startMin));
};

export const normalizeResources = (
  resources: CalendarResource[],
  date: Date,
  cfg: MCGridConfig,
): MCResource[] => {
  const weekday = date.getDay();
  // Match current behavior: resourceOrder="id".
  const sorted = [...resources].sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { numeric: true }),
  );
  return sorted.map((resource, index) => {
    const business = businessRangesForDay(resource.businessHours, weekday, cfg);
    // "Empty business hours" fallback: a resource without any business hours
    // stays fully usable (no dimming), mirroring the old calendar semantics.
    let nonBusiness: MCTimeRange[] = [];
    if (business.length > 0) {
      let cursor = cfg.dayStartMin;
      for (const range of business) {
        if (range.startMin > cursor) nonBusiness.push({ startMin: cursor, endMin: range.startMin });
        cursor = Math.max(cursor, range.endMin);
      }
      if (cursor < cfg.dayEndMin) nonBusiness.push({ startMin: cursor, endMin: cfg.dayEndMin });
    }
    return {
      id: resource.id,
      title: resource.title,
      index,
      warningCount: resource.extendedProps?.warningCount,
      nonBusinessSegments: nonBusiness,
    };
  });
};

// ---------------------------------------------------------------------------
// Event normalization
// ---------------------------------------------------------------------------

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const eventResourceId = (event: CalendarEvent): string | undefined => {
  const anyEvent = event as CalendarEvent & { resourceId?: string; resourceIds?: string[] };
  return anyEvent.resourceId ?? anyEvent.resourceIds?.[0];
};

// ---------------------------------------------------------------------------
// Lane layout (overlap columns) — classic cluster sweep, O(n log n) / resource
// ---------------------------------------------------------------------------

const layoutLanes = (events: MCEvent[]): void => {
  // events are sorted by startMin (then longer first)
  let active: { endMin: number; lane: number }[] = [];
  let cluster: MCEvent[] = [];
  let clusterLanes = 0;

  const closeCluster = () => {
    for (const e of cluster) e.laneCount = clusterLanes;
    cluster = [];
    clusterLanes = 0;
  };

  for (const event of events) {
    active = active.filter((a) => a.endMin > event.startMin);
    if (active.length === 0 && cluster.length > 0) closeCluster();

    const used = new Set(active.map((a) => a.lane));
    let lane = 0;
    while (used.has(lane)) lane++;

    event.lane = lane;
    active.push({ endMin: event.endMin, lane });
    cluster.push(event);
    clusterLanes = Math.max(clusterLanes, lane + 1);
  }
  if (cluster.length > 0) closeCluster();
};

// ---------------------------------------------------------------------------
// Index builder
// ---------------------------------------------------------------------------

export const buildEventIndex = (
  calendarEvents: CalendarEvent[],
  resources: MCResource[],
  cfg: MCGridConfig,
  date: Date,
): MCEventIndex => {
  const t0 = performance.now();

  const weekday = date.getDay();
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  const resourceIds = new Set(resources.map((r) => r.id));
  const events: MCEvent[] = [];
  const background: MCBackgroundSegment[] = [];

  for (const raw of calendarEvents) {
    const display = (raw.display as string) ?? (raw.extendedProps?.display as string | undefined);
    if (display === "none") continue; // hidden group-patient slots
    if (isGroupPatientType(raw.extendedProps?.timeSlotType)) continue;

    const resourceId = eventResourceId(raw);
    if (!resourceId || !resourceIds.has(resourceId)) continue;

    const start = toDate(raw.start);
    const end = toDate(raw.end);
    if (!start || !end) continue;

    // FullCalendar recurring semantics: events carrying `daysOfWeek` are
    // weekly templates (continuous appointments / derived lunch breaks whose
    // concrete start/end sit on some other day of the work-plan week). They
    // materialize only on matching weekdays; everything else must actually
    // intersect the displayed day.
    const recurringDays = (raw as { daysOfWeek?: Array<string | number> }).daysOfWeek;
    if (recurringDays && recurringDays.length > 0) {
      if (!recurringDays.some((d) => Number(d) === weekday)) continue;
    } else if (end <= dayStart || start >= dayEnd) {
      continue;
    }

    const startRawMin = start.getHours() * 60 + start.getMinutes();
    const endRawMin = end.getHours() * 60 + end.getMinutes();
    const startMin = Math.max(startRawMin, cfg.dayStartMin);
    const endMin = Math.min(endRawMin <= startRawMin ? cfg.dayEndMin : endRawMin, cfg.dayEndMin);
    if (endMin <= cfg.dayStartMin || startMin >= cfg.dayEndMin) continue;

    const props = raw.extendedProps;
    const id = String(raw.id ?? `${resourceId}-${startRawMin}-${props?.timeSlotId ?? ""}`);
    if (display === "background") {
      background.push({
        id,
        resourceId,
        startMin,
        endMin,
        kind:
          props?.eventType === "availability" || props?.timeSlotType === "PatientSlot"
            ? "availability"
            : props?.containsHeatTreatment || (raw as any).isHeatTreatment
              ? "heat"
              : "generic",
        title: typeof raw.title === "string" ? raw.title : undefined,
      });
      continue;
    }

    const isAppointment = props?.eventType === "appointment";
    const rx = props?.rx as
      | { date?: unknown; rxNumber?: unknown; isPerpetual?: boolean }
      | undefined;
    const isGroupTherapist = isGroupTherapistType(props?.timeSlotType);
    const eventType = props?.eventType ?? "timeslot";
    const timeSlotType = props?.timeSlotType ?? "TimeSlot";
    const title = typeof raw.title === "string" ? raw.title : "";
    let participantNames: string[] | undefined;
    if (isGroupTherapist) {
      const participants = (props?.participants ?? []) as Array<{
        timeSlots?: Array<{ therapyRx?: { rx?: { patient?: { lastName?: string; firstName?: string } } } }>;
      }>;
      participantNames = participants.map((p) => {
        const patient = p?.timeSlots?.[0]?.therapyRx?.rx?.patient;
        return patient?.lastName
          ? `${patient.lastName}${patient.firstName ? `, ${patient.firstName}` : ""}`
          : "Patient";
      });
    }

    events.push({
      id,
      resourceId,
      startMin,
      endMin: Math.max(endMin, startMin + cfg.slotMin),
      startLabel: minutesToTime(startRawMin),
      endLabel: minutesToTime(endRawMin),
      title,
      kind: kindOfEvent(eventType, timeSlotType, isGroupTherapist, title),
      eventType,
      timeSlotType,
      isCancelled: Boolean(props?.isCancelled),
      attended: Boolean(props?.attended),
      hasNotes: Boolean(props?.hasNotes),
      containsHeatTreatment: Boolean(props?.containsHeatTreatment),
      isContinuous: Boolean(props?.isContinuous),
      isOutsideWorkingHours: Boolean(props?.isOutsideWorkingHours),
      isDraft: props?.eventType === "draft",
      isGroupTherapist,
      patientId: props?.patient?.id,
      patientLabel: props?.patient?.displayName || props?.patient?.fullName,
      appointmentId: props?.appointmentId,
      therapyAbbr: props?.therapy?.abbreviation,
      therapyColorKey: props?.therapy?.colorKey,
      treatmentIndex: props?.treatmentIndex,
      visitType: props?.visitType,
      visitFeeAbbr: (props?.visitFee as { abbreviation?: string } | undefined)?.abbreviation,
      sublocalityAbbr: props?.sublocality?.abbreviation ?? undefined,
      sublocalityColor: props?.sublocality?.color ?? undefined,
      sublocalityName: props?.sublocality?.name ?? undefined,
      // Rx status, mirroring EventContent precedence: cart when the Rx has
      // neither date nor number; danger when undated but numbered (and the
      // series is not continuous).
      rxInCart: isAppointment && !rx?.date && !rx?.rxNumber,
      rxMissing:
        isAppointment && !rx?.date && Boolean(rx?.rxNumber) && !Boolean(props?.isContinuous),
      rxPerpetual: Boolean(rx?.isPerpetual),
      wasDocumented: (props?.wasDocumentedCount ?? 0) > 0,
      cancelReason: typeof props?.reason === "string" ? props.reason : undefined,
      participantNames,
      lane: 0,
      laneCount: 1,
      raw,
    });
  }

  // Group per resource, sort, lay out lanes.
  const byResource = new Map<string, MCEvent[]>();
  for (const event of events) {
    let list = byResource.get(event.resourceId);
    if (!list) byResource.set(event.resourceId, (list = []));
    list.push(event);
  }
  for (const list of byResource.values()) {
    list.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
    layoutLanes(list);
  }

  const backgroundByResource = new Map<string, MCBackgroundSegment[]>();
  for (const segment of background) {
    let list = backgroundByResource.get(segment.resourceId);
    if (!list) backgroundByResource.set(segment.resourceId, (list = []));
    list.push(segment);
  }

  const byId = new Map<string, MCEvent>();
  const byPatientId = new Map<number, string[]>();
  const byAppointmentId = new Map<string, string[]>();
  for (const event of events) {
    byId.set(event.id, event);
    if (event.patientId != null) {
      let ids = byPatientId.get(event.patientId);
      if (!ids) byPatientId.set(event.patientId, (ids = []));
      ids.push(event.id);
    }
    if (event.appointmentId) {
      let ids = byAppointmentId.get(event.appointmentId);
      if (!ids) byAppointmentId.set(event.appointmentId, (ids = []));
      ids.push(event.id);
    }
  }

  return {
    events,
    background,
    byId,
    byResource,
    backgroundByResource,
    byPatientId,
    byAppointmentId,
    buildMs: performance.now() - t0,
  };
};

/** Events of one resource (stable empty fallback for memoized columns). */
export const eventsForResource = (
  index: MCEventIndex,
  resourceId: string,
): ReadonlyArray<MCEvent> => index.byResource.get(resourceId) ?? EMPTY_EVENTS;

export const backgroundForResource = (
  index: MCEventIndex,
  resourceId: string,
): ReadonlyArray<MCBackgroundSegment> =>
  index.backgroundByResource.get(resourceId) ?? EMPTY_SEGMENTS;

/**
 * Topmost event overlapping a cell — binary search on start, short backward
 * scan (events per resource are sorted by startMin).
 */
export const eventAtCell = (
  index: MCEventIndex,
  resourceId: string,
  min: number,
): MCEvent | null => {
  const list = index.byResource.get(resourceId);
  if (!list?.length) return null;

  // upper bound: first index with startMin > min
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (list[mid].startMin > min) hi = mid;
    else lo = mid + 1;
  }
  // scan backwards for an event still covering `min`; prefer the latest start
  for (let i = lo - 1; i >= 0; i--) {
    const event = list[i];
    if (event.startMin <= min && event.endMin > min) return event;
    // events are sorted by start; once starts are far in the past we can stop
    // (bounded by the longest plausible event of 12h)
    if (min - event.startMin > 12 * 60) break;
  }
  return null;
};
